import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Booking } from "@/types/database";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { validateBooking, type BookingValidationResult } from "@/lib/bookingValidator";
import { sendBookingConfirmationEmail, sendBookingApprovedEmail } from "@/lib/email";
import { notifyHostBookingEvent, notifyManagersNewBooking } from "@/lib/notifications";
import { logActivity } from "@/lib/activityLogger";
import { trackEvent } from "@/lib/analytics";
import { BookingError, reportError, errorToastFor } from "@/lib/errorHandler";

const BOOKINGS_SELECT = `
  *,
  unit:units!bookings_unit_id_fkey(*),
  host:hosts!bookings_host_id_fkey(
    *,
    profile:profiles!hosts_user_id_fkey(*)
  ),
  guest_token:guest_tokens!guest_tokens_booking_id_fkey(id, token, expires_at, id_uploaded, rules_acknowledged, access_code)
`;

/**
 * Used by dashboards/reports that need the full booking set for stats.
 * Capped at 500 rows (instead of being fully unbounded) so a strata with a
 * very large booking history can't pull down an unbounded result set.
 * For an actual list UI with paging controls, use useBookingsPage instead.
 */
export function useBookings(statusFilter?: string, options?: { enabled?: boolean }) {
  const { activeOrgId } = useAuth();

  return useQuery({
    queryKey: ["bookings", activeOrgId, statusFilter],
    enabled: !!activeOrgId && (options?.enabled ?? true),
    queryFn: async () => {
      let query = supabase
        .from("bookings")
        .select(BOOKINGS_SELECT)
        .eq("organization_id", activeOrgId!)
        .order("created_at", { ascending: false })
        .range(0, 499);

      if (statusFilter && statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Booking[];
    },
  });
}

/** Page-based version of useBookings for list UIs with Previous/Next controls. */
export function useBookingsPage(
  statusFilter: string | undefined,
  page: number,
  pageSize: number,
  options?: { enabled?: boolean }
) {
  const { activeOrgId } = useAuth();

  return useQuery({
    queryKey: ["bookings-page", activeOrgId, statusFilter, page, pageSize],
    enabled: !!activeOrgId && (options?.enabled ?? true),
    queryFn: async () => {
      const from = page * pageSize;
      const to = from + pageSize - 1;

      let query = supabase
        .from("bookings")
        .select(BOOKINGS_SELECT, { count: "exact" })
        .eq("organization_id", activeOrgId!)
        .order("created_at", { ascending: false })
        .range(from, to);

      if (statusFilter && statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      const { data, error, count } = await query;
      if (error) throw error;
      return { bookings: data as Booking[], totalCount: count ?? 0 };
    },
  });
}

export function useCreateBooking() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user, activeOrgId } = useAuth();

  return useMutation({
    mutationFn: async (booking: {
      unit_id: string;
      host_id: string;
      guest_name: string;
      guest_email?: string;
      guest_phone?: string;
      guest_count: number;
      check_in: string;
      check_out: string;
      nights: number;
      fee: number;
      platform?: string;
      booking_reference?: string;
      vehicle_info?: string;
      purpose?: string;
      booking_notes?: string;
    }): Promise<{ booking: any; validation: BookingValidationResult | null }> => {
      if (!activeOrgId) throw new Error("No active organization");

      // 1. Insert booking as "pending"
      const { data, error } = await supabase
        .from("bookings")
        .insert({ ...booking, organization_id: activeOrgId, status: "pending" })
        .select()
        .single();
      if (error) throw error;

      // 2. Fetch host data for validation
      const { data: host } = await supabase
        .from("hosts")
        .select("*, unit:units!hosts_unit_id_fkey(*)")
        .eq("id", booking.host_id)
        .single();

      // 3. Fetch strata config
      const { data: config } = await supabase
        .from("strata_config")
        .select("*")
        .eq("organization_id", activeOrgId)
        .single();

      let validation: BookingValidationResult | null = null;

      // 4. Run automated validation if we have host + config data
      if (!host || !config) {
        console.warn("[useBookings] Booking created without validation — host or strata config unavailable");
      }
      if (host && config) {
        validation = validateBooking(
          {
            nights: booking.nights,
            guestCount: booking.guest_count,
            checkIn: booking.check_in,
            checkOut: booking.check_out,
            unitNumber: host.unit?.unit_number ?? "",
            hostStatus: host.status,
            hostExpiresAt: host.expires_at,
            hostNightsUsed: host.nights_used ?? 0,
            hostNightLimit: host.night_limit ?? config.max_nights_per_year,
          },
          config
        );

        // 5. Update booking status based on validation
        await supabase
          .from("bookings")
          .update({
            status: validation.status,
            blocked_reason: validation.blockedReason ?? null,
          })
          .eq("id", data.id);

        // 6. If approved, increment host's nights_used
        if (validation.isValid) {
          await supabase
            .from("hosts")
            .update({ nights_used: (host.nights_used ?? 0) + booking.nights })
            .eq("id", booking.host_id);

          // 6a. Send booking confirmation + host notification emails (fire-and-forget)
          if (booking.guest_email) {
            // Fetch the full booking with guest_token (created by DB trigger) and org name
            const { data: fullBooking } = await supabase
              .from("bookings")
              .select(`
                *,
                unit:units!bookings_unit_id_fkey(unit_number),
                host:hosts!bookings_host_id_fkey(
                  *,
                  profile:profiles!hosts_user_id_fkey(full_name, email)
                ),
                guest_token:guest_tokens!guest_tokens_booking_id_fkey(token),
                organization:organizations!bookings_organization_id_fkey(name)
              `)
              .eq("id", data.id)
              .single();

            if (fullBooking) {
              const guestTokens = fullBooking.guest_token as any;
              const token: string | undefined = Array.isArray(guestTokens)
                ? guestTokens[0]?.token
                : guestTokens?.token;
              const orgName = (fullBooking.organization as any)?.name ?? "Strata";
              const unitNumber = (fullBooking.unit as any)?.unit_number ?? "—";
              const hostProfile = (fullBooking.host as any)?.profile;
              const appUrl = window.location.origin;

              if (token) {
                sendBookingConfirmationEmail({
                  guestEmail: booking.guest_email,
                  guestName: booking.guest_name,
                  unitNumber,
                  orgName,
                  checkIn: booking.check_in,
                  checkOut: booking.check_out,
                  nights: booking.nights,
                  guestCount: booking.guest_count,
                  guestPortalUrl: `${appUrl}/guest/${token}`,
                  hostName: hostProfile?.full_name,
                  bookingId: data.id,
                }).catch((e) => {
                  const err = new BookingError(
                    "EMAIL",
                    { operation: "send_booking_confirmation_email", bookingId: data.id, userId: user?.id },
                    e instanceof Error ? e.message : String(e)
                  );
                  reportError(err);
                  toast({ ...errorToastFor(err), description: `${errorToastFor(err).description} (booking still created)` });
                });
              }

              if (hostProfile?.email) {
                sendBookingApprovedEmail({
                  hostEmail: hostProfile.email,
                  hostName: hostProfile.full_name,
                  guestName: booking.guest_name,
                  guestEmail: booking.guest_email,
                  guestCount: booking.guest_count,
                  unitNumber,
                  orgName,
                  checkIn: booking.check_in,
                  checkOut: booking.check_out,
                  nights: booking.nights,
                  dashboardUrl: `${appUrl}/bookings`,
                  bookingId: data.id,
                }).catch((e) => {
                  const err = new BookingError(
                    "EMAIL",
                    { operation: "send_booking_approved_email_host", bookingId: data.id, userId: user?.id },
                    e instanceof Error ? e.message : String(e)
                  );
                  reportError(err);
                  toast({ ...errorToastFor(err), description: `${errorToastFor(err).description} (booking still created)` });
                });
              }
            }
          }
        }

        // 7. Log the validation result
        await supabase.from("audit_logs").insert({
          organization_id: activeOrgId,
          action: `Booking auto-${validation.status}`,
          entity: `Booking ${data.id}`,
          detail: validation.isValid
            ? `All ${validation.checks.length} checks passed — booking approved`
            : `Validation failed: ${validation.blockedReason}`,
          user_name: "System (Auto-Validator)",
        });

        // 8. In-app notification to host (fire-and-forget)
        if (host.user_id) {
          notifyHostBookingEvent(
            host.user_id,
            activeOrgId,
            data.id,
            validation.isValid ? "approved" : "blocked",
            booking.guest_name,
            booking.check_in,
            validation.blockedReason ?? undefined
          ).catch((e) => console.warn("[notify] Booking host notification failed:", e));
        }

        // 9. Notify managers of new booking (fire-and-forget)
        const unitNumber = host.unit?.unit_number ?? "—";
        notifyManagersNewBooking(
          activeOrgId,
          data.id,
          unitNumber,
          booking.guest_name,
          booking.check_in,
          booking.check_out
        ).catch((e) => console.warn("[notify] Booking manager notification failed:", e));
      }

      // 10. Log the creation
      await supabase.from("audit_logs").insert({
        organization_id: activeOrgId,
        action: "Booking created",
        entity: `Booking ${data.id}`,
        detail: `Guest: ${booking.guest_name}, ${booking.check_in} to ${booking.check_out}`,
        user_name: user?.fullName ?? "System",
      });

      return { booking: data, validation };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["bookings", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["booking-stats", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["audit_logs", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["hosts", activeOrgId] });

      if (activeOrgId) {
        const actionType = result.validation?.isValid ? "booking_approved"
          : result.validation ? "booking_blocked"
          : "booking_created";
        logActivity({
          orgId: activeOrgId, userId: user?.id, actionType,
          entityType: "booking", entityId: result.booking.id,
          metadata: { guest_name: result.booking.guest_name, nights: result.booking.nights },
        });
      }
      queryClient.invalidateQueries({ queryKey: ["activity_feed", activeOrgId] });

      if (result.validation) {
        if (result.validation.isValid) {
          trackEvent("booking_approved", { org_id: activeOrgId ?? undefined, nights: result.booking.nights });

          // Fire onboarding_completed on the user's first approved booking.
          // localStorage flag prevents re-firing on subsequent bookings.
          if (user?.id) {
            const flagKey = `sg_onboarded_${user.id}`;
            if (!localStorage.getItem(flagKey)) {
              localStorage.setItem(flagKey, "1");
              trackEvent("onboarding_completed", { org_id: activeOrgId ?? undefined, trigger: "first_booking_approved" });
            }
          }

          toast({
            title: "Booking approved ✓",
            description: `All ${result.validation.checks.length} compliance checks passed. Booking has been automatically approved.`,
          });
        } else {
          trackEvent("booking_blocked", { org_id: activeOrgId ?? undefined, reason: result.validation.blockedReason ?? "unknown" });
          toast({
            title: "Booking blocked",
            description: result.validation.blockedReason ?? "Booking failed compliance checks",
            variant: "destructive",
          });
        }
      } else {
        trackEvent("booking_created", { org_id: activeOrgId ?? undefined, nights: result.booking.nights });
        toast({ title: "Booking created", description: "The booking has been submitted (manual review required)." });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}

export function useUpdateBookingStatus() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user, activeOrgId } = useAuth();

  return useMutation({
    mutationFn: async ({ id, status, blocked_reason }: { id: string; status: string; blocked_reason?: string }) => {
      // Fetch current status before updating to enforce legal transitions
      const { data: current } = await supabase
        .from("bookings")
        .select("status")
        .eq("id", id)
        .single();

      const terminal = new Set(["completed", "cancelled"]);
      if (current && terminal.has(current.status)) {
        throw new Error(`Cannot change status of a ${current.status} booking`);
      }

      const { data, error } = await supabase
        .from("bookings")
        .update({ status, blocked_reason })
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;

      await supabase.from("audit_logs").insert({
        organization_id: activeOrgId,
        action: `Booking ${status}`,
        entity: `Booking ${id}`,
        detail: blocked_reason || `Booking status changed to ${status}`,
        user_name: user?.fullName ?? "System",
      });

      // ── Send emails + in-app notification when manually approved ────────
      if (status === "approved") {
        const { data: fullBooking } = await supabase
          .from("bookings")
          .select(`
            *,
            unit:units!bookings_unit_id_fkey(unit_number),
            host:hosts!bookings_host_id_fkey(
              *,
              profile:profiles!hosts_user_id_fkey(full_name, email)
            ),
            guest_token:guest_tokens!guest_tokens_booking_id_fkey(token),
            organization:organizations!bookings_organization_id_fkey(name)
          `)
          .eq("id", id)
          .single();

        if (fullBooking) {
          const guestTokens = fullBooking.guest_token as any;
          const token: string | undefined = Array.isArray(guestTokens)
            ? guestTokens[0]?.token
            : guestTokens?.token;
          const orgName = (fullBooking.organization as any)?.name ?? "Strata";
          const unitNumber = (fullBooking.unit as any)?.unit_number ?? "—";
          const hostRecord = fullBooking.host as any;
          const hostProfile = hostRecord?.profile;
          const appUrl = window.location.origin;

          if (fullBooking.guest_email && token) {
            sendBookingConfirmationEmail({
              guestEmail: fullBooking.guest_email,
              guestName: fullBooking.guest_name,
              unitNumber,
              orgName,
              checkIn: fullBooking.check_in,
              checkOut: fullBooking.check_out,
              nights: fullBooking.nights,
              guestCount: fullBooking.guest_count,
              guestPortalUrl: `${appUrl}/guest/${token}`,
              hostName: hostProfile?.full_name,
              bookingId: id,
            }).catch((e) => {
              const err = new BookingError(
                "EMAIL",
                { operation: "send_booking_confirmation_email", bookingId: id, userId: user?.id },
                e instanceof Error ? e.message : String(e)
              );
              reportError(err);
              toast({ ...errorToastFor(err), description: `${errorToastFor(err).description} (status update still saved)` });
            });
          }

          if (hostProfile?.email) {
            sendBookingApprovedEmail({
              hostEmail: hostProfile.email,
              hostName: hostProfile.full_name,
              guestName: fullBooking.guest_name,
              guestEmail: fullBooking.guest_email,
              guestCount: fullBooking.guest_count,
              unitNumber,
              orgName,
              checkIn: fullBooking.check_in,
              checkOut: fullBooking.check_out,
              nights: fullBooking.nights,
              dashboardUrl: `${appUrl}/bookings`,
              bookingId: id,
            }).catch((e) => {
              const err = new BookingError(
                "EMAIL",
                { operation: "send_booking_approved_email_host", bookingId: id, userId: user?.id },
                e instanceof Error ? e.message : String(e)
              );
              reportError(err);
              toast({ ...errorToastFor(err), description: `${errorToastFor(err).description} (status update still saved)` });
            });
          }

          // In-app notification to host (fire-and-forget)
          if (hostRecord?.user_id && activeOrgId) {
            notifyHostBookingEvent(
              hostRecord.user_id,
              activeOrgId,
              id,
              "approved",
              fullBooking.guest_name,
              fullBooking.check_in
            ).catch((e) => console.warn("[notify] Manual approval notification failed:", e));
          }
        }
      }

      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["bookings", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["audit_logs", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["activity_feed", activeOrgId] });
      toast({ title: `Booking ${variables.status}`, description: `The booking has been ${variables.status}.` });
      if (activeOrgId) {
        const actionType = variables.status === "approved" ? "booking_approved"
          : variables.status === "rejected" ? "booking_rejected"
          : variables.status === "blocked" ? "booking_blocked"
          : "booking_cancelled";
        trackEvent(actionType as any, { org_id: activeOrgId });
        logActivity({
          orgId: activeOrgId, userId: user?.id, actionType,
          entityType: "booking", entityId: variables.id,
        });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}

export function useBookingStats() {
  const { activeOrgId } = useAuth();

  return useQuery({
    queryKey: ["booking-stats", activeOrgId],
    enabled: !!activeOrgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select("status")
        .eq("organization_id", activeOrgId!);
      if (error) throw error;

      const total = data.length;
      const approved = data.filter((b) => b.status === "approved").length;
      const blocked = data.filter((b) => b.status === "blocked").length;
      const pending = data.filter((b) => b.status === "pending").length;
      const completed = data.filter((b) => b.status === "completed").length;

      return { total, approved, blocked, pending, completed };
    },
  });
}
